import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { MapRange, ThreatMapCoverageData, ThreatMapData } from '@/types/map'

interface ThreatMapQueryOptions {
  enabled?: boolean
}

/**
 * SP6 threat-map aggregation feed. Backed by GET /api/stories/map?days=N
 * (apps/api/src/routes/stories-map.routes.ts). Polls every 30s and treats
 * responses as fresh for the same window so the Globe/Region/Country views
 * don't re-layout on focus changes.
 *
 * @param rangeDays Whole number of days of history (1..30). Server-side Zod
 *   validates the bounds.
 */
export function useThreatMap(rangeDays: MapRange, options: ThreatMapQueryOptions = {}) {
  const enabled = options.enabled ?? true
  return useQuery({
    queryKey: ['map', rangeDays],
    queryFn: () => api.get<ThreatMapData>(`/stories/map?days=${rangeDays}`),
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 30_000 : false,
  })
}

/**
 * Aggregate-only map coverage feed. Backed by
 * GET /api/stories/map/coverage?days=N. This endpoint intentionally returns
 * posture counts/stages/queues without raw incident payloads, so SOC View can
 * render wallboard metrics without guessing from display strings.
 */
export function useThreatMapCoverage(rangeDays: MapRange, enabled = true) {
  return useQuery({
    queryKey: ['map', 'coverage', rangeDays],
    queryFn: () => api.get<ThreatMapCoverageData>(`/stories/map/coverage?days=${rangeDays}`),
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 30_000 : false,
  })
}

/**
 * Combined threat-map + coverage feed for SOC mode. Backed by
 * GET /api/stories/map?days=N&includeCoverage=true. This endpoint returns both
 * the full map data and coverage metrics in a single request, eliminating the
 * duplicate aggregation that occurs when fetching them separately.
 */
export function useThreatMapWithCoverage(rangeDays: MapRange, enabled = true) {
  return useQuery({
    queryKey: ['map', 'with-coverage', rangeDays],
    queryFn: () => api.get<{ map: ThreatMapData; coverage: ThreatMapCoverageData }>(`/stories/map?days=${rangeDays}&includeCoverage=true`),
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 30_000 : false,
  })
}
